import type { StyleObject } from '@/typings/secure-fields';

export const enum Environment {
  Production = 'production',
  Stage = 'stage',
  Dukenukem = 'dukenukem',
  Development = 'development'
}

// Property default language code
export const defaultLanguageCode = 'en-US';
export const defaultCurrencyCode = 'EUR';
export const accessTokenCookieName = 'bobw-jwt-token';
export const helpEmail = 'hello@bobw.co';
export const businessEmail = 'biz@bobw.co';
export const loyaltyVoucher = 'innercircle';

export const innerCircleRateToggledQueryParam = 'isInnerCircleRateToggled';
export const freeCancellationToggledQueryParam = 'isFreeCancellationToggled';
export const voucherQueryParam = 'voucherCode';
export const allocationQueryParam = 'allocation';
// Query params for rate metrics tracking
export const listingPriceAtQueryParam = 'listingPriceAt';
export const apartmentDetailsPriceAtQueryParam = 'apartmentDetailsPriceAt';
// Query param for booking.success metrics tracking
export const bookingCreationSuccessMetricQueryParam = 'bcs';
// Derbysoft tracking parameter, required for commercial purposes
export const dsclIdQueryParam = 'dsclid';
export const sortQueryParam = 'sort';

export const guestBookUtmSource = 'theguestbook';
export const mainContentRefKey = 'mainContentRef';

// Language cookie name
export const languageCookieName = 'selectedLanguage';

// Booking failed attempts cookie (masked reservationGroupId + count, max-age 1 day)
export const bookingFailedAttemptsCookieName = 'bobw-booking-failed-attempts';

// {X}% discount for inner circle members
export const defaultInnerCircleDiscount = 5;
export const referralInnerCircleDiscount = 10;

export const globalReviewScore = 4.4;
export const globalReviewCount = 48_000;

export const environmentNames = {
  [Environment.Production]: 'prod',
  [Environment.Stage]: 'stage',
  [Environment.Dukenukem]: 'dukenukem',
  [Environment.Development]: 'development'
} as const;

export const baseUrls = {
  [Environment.Production]: 'https://bobw.co',
  [Environment.Stage]: 'https://staging-bobw.com',
  [Environment.Dukenukem]: 'https://qa-bobw-30bea2.xperience-sites.com',
  [Environment.Development]: 'http://localhost:3001'
} as const;

export const baseBookingUrls = {
  [Environment.Production]: 'https://booking.bobw.co',
  [Environment.Stage]: 'https://booking.staging-bobw.com',
  [Environment.Dukenukem]: 'https://booking.dukenukem.test-bobw.com',
  [Environment.Development]: 'http://localhost:3001'
} as const;

export const coreApiBaseUrls = {
  [Environment.Production]: 'https://core-api.bobw.co',
  [Environment.Stage]: 'https://core-api.lb.staging-bobw.com',
  [Environment.Dukenukem]: 'https://core-api.lb.dukenukem.test-bobw.com',
  [Environment.Development]: 'http://localhost:3030'
} as const;

export const checkInBaseUrls = {
  [Environment.Production]: 'https://guest.bobw.co',
  [Environment.Stage]: 'https://guest.staging-bobw.com',
  [Environment.Dukenukem]: 'https://guest.dukenukem.test-bobw.com',
  [Environment.Development]: 'http://localhost:5002'
} as const;

export const receptionBaseUrls = {
  [Environment.Production]: 'https://reception.bobw.co',
  [Environment.Stage]: 'https://reception.staging-bobw.com',
  [Environment.Dukenukem]: 'https://reception.dukenukem.test-bobw.com',
  [Environment.Development]: 'http://localhost:3000'
} as const;

export const secureFieldsHosts = {
  [Environment.Production]: 'pay.datatrans.com',
  [Environment.Stage]: 'pay.sandbox.datatrans.com',
  [Environment.Dukenukem]: 'pay.sandbox.datatrans.com',
  [Environment.Development]: 'pay.sandbox.datatrans.com'
} as const;

/*
 * Test account has been created for roman@bobw.co with tag id "GTM-5JRNN6G".
 * It could be changed to anything else during testing.
 */
export const gtmIds = {
  [Environment.Production]: 'GTM-M57TPL5',
  [Environment.Stage]: 'GTM-5JRNN6G',
  [Environment.Dukenukem]: 'GTM-5JRNN6G',
  [Environment.Development]: 'GTM-5JRNN6G'
} as const;

export const turnstileSiteKeys = {
  [Environment.Production]: '0x4AAAAAAAg5ZgJYolrIRZel',
  [Environment.Stage]: '0x4AAAAAAAg5ZFPZnqqIPBiJ',
  [Environment.Dukenukem]: '0x4AAAAAAAg5ZQ7WkSDXfbS6',
  [Environment.Development]: '0x4AAAAAAAg5ZQ7WkSDXfbS6'
} as const;

export const innerCirclePaths = {
  signUp: '/signup',
  logIn: '/login',
  account: '/account'
};

export const secureFieldsPaymentMethods = {
  visa: 'VIS',
  mastercard: 'ECA',
  amex: 'AMX',
  diners: 'DIN',
  discover: 'DIS',
  jcb: 'JCB',
  elo: 'ELO',
  unionpay: 'CUP'
} as const;

export const secureFieldsStyles: StyleObject = {
  '@font-face': {
    '*': {
      fontFamily: 'TheFuture',
      fontWeight: 400,
      fontStyle: 'normal',
      fontDisplay: 'auto',
      src: 'url("TheFuture-Regular.ttf") format("truetype")'
    }
  },

  '*': {
    fontFamily: 'TheFuture, sans-serif',
    fontSize: '1rem',
    lineHeight: '1.5rem',
    padding: '1.75rem 1rem 0.75rem 1.5rem',
    background: 'none',
    border: 'none',
    outline: 'none',
    width: '100%'
  }
};

export const defaultGuestCounts = {
  adults: 1,
  children: 0
};

// TODO: rely on backend to deliver max count
export const maxInfantsPerUnit = 5;

export const globalReviewProperties = [
  // Bob W Commercial House, london-cs43
  'daf8a9c1-f55f-4a8e-8511-aed8009e9c96',
  // Bob W Koti Ullanlinna, helsinki-pun4
  '5e393d16-2255-47bd-a85d-af86009cc048',
  // Bob W Telliskivi, bob-w-telliskivi
  '4d1805ca-8676-484e-872d-aa6400cf60cf',
  // Bob W Gamle Oslo, oslo-gro16
  '0fd902b4-889d-47f4-931e-af97009fc006',
  // Bob W Turku City Center, turku-eer12
  'c25ddc97-d2fa-4e96-8319-b0c800f089fa'
];

export const ucmStorageKey = 'uc_gcm';

export const isLanguageDisabled = true;
export const isPropertiesCacheDisabled = true;

// HRZ-2090: hide quick filters until unit-category feature data is reliable.
export const hideQuickFilters = true;
